/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES DURÉES À L'ÉCRAN — DES SECONDES EN BASE, DES HEURES À LA LECTURE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * L'API rend **des secondes entières**, partout et sans exception : c'est la seule unité
 * qui ne perde rien et qui ne suppose rien de l'écran. La mise en forme est donc un
 * travail de front, et il vit ici plutôt que recopié dans chaque page — **la même durée
 * lue deux fois avec deux arrondis différents ferait douter du compte lui-même**, et c'est
 * précisément le compte que la structure facture.
 *
 * **CE FICHIER NE RANGE RIEN DANS `shared/` DU DÉPÔT, ET C'EST DÉLIBÉRÉ.** `shared/lib/`
 * porte « la logique métier pure » — une règle que les deux côtés **rejouent**. Mettre en
 * forme une durée n'est pas une règle : le serveur ne la rejoue pas, il n'a pas d'écran.
 */

/**
 * **Une durée en secondes, telle qu'une personne la lit.**
 *
 * `DESIGN.md` fixe le ton : « **le produit dit ce qui s'est passé, sans commenter** ».
 * Donc `1 h 05`, pas « environ une heure », et pas `3 900 s`.
 *
 * **Zéro se dit, il ne se cache pas** (F-46) : « un cours où personne n'est jamais entré
 * porte sa ligne, avec sa date, ses participants, **une durée de zéro** ». Une case vide
 * laisserait croire à une donnée manquante là où le fait est qu'il ne s'est rien passé.
 */
export function dureeLisible(secondes: number): string {
  if (secondes <= 0) return '0 min';
  const heures = Math.floor(secondes / 3600);
  const minutes = Math.floor((secondes % 3600) / 60);
  if (heures === 0) {
    // **Sous la minute, on arrondit à la minute la plus proche et jamais à zéro** : une
    // présence de quarante secondes est une présence, et « 0 min » dirait le contraire.
    return `${Math.max(1, minutes)} min`;
  }
  return `${heures} h ${String(minutes).padStart(2, '0')}`;
}

/**
 * **Un instant, tel qu'une personne le lit** — date et heure de début, F-46.
 *
 * **Les dates sont en temps universel en base, et converties À L'AFFICHAGE UNIQUEMENT**
 * (`CLAUDE.md`). C'est ici que la conversion a lieu, et **c'est le seul endroit** : la
 * faire dans une page la ferait diverger d'un écran à l'autre.
 */
export function instantLisible(iso: string): string {
  const quand = new Date(iso);
  if (Number.isNaN(quand.getTime())) return '—';
  return quand.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** La date seule, sans l'heure — pour un rattachement ou un changement d'adresse. */
export function dateLisible(iso: string): string {
  const quand = new Date(iso);
  if (Number.isNaN(quand.getTime())) return '—';
  return quand.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
